<template>
  <div class="media-player-controls">
    <div class="media-player-controls__controls">
      <BaseDropdownWrapper
        width="fill"
        :with-rounded="true"
        :with-surface="false"
        :with-overflow="false"
        max-height="auto"
        placement="top-start"
        bg-color="transparent"
        :offset-y="18"
        :offset-x="-12"
      >
        <template #control>
          <BaseIcon
            name="vuesax:play-speed-bold"
            fill-type="solid"
            color="var(--color-foreground-inverted)"
            size="24px"
            clickable
          />
        </template>

        <template #dropdown>
          <BaseIsland variant="transparent-inverted-200" size="sm" :with-shadow="false">
            <BaseGroupActions gap="xs" direction="column">
              <BaseButton
                v-for="speed in speedsMap"
                :key="speed.name"
                size="mini"
                :variant="currentSpeed === speed.name ? 'dark' : 'transparent'"
                with-full
                @click="handleChangeSpeed(speed)"
              >
                {{ speedsMap[speed.name].label }}
              </BaseButton>
            </BaseGroupActions>
          </BaseIsland>
        </template>
      </BaseDropdownWrapper>
      <BaseIcon
        :name="props.isPlaying ? 'vuesax:pause-bold' : 'vuesax:play-mdi-bold'"
        fill-type="solid"
        color="var(--color-foreground-inverted)"
        size="24px"
        clickable
        @click="emit('togglePlay')"
      />
      <BaseDropdownWrapper
        v-if="!props.isLocal"
        width="fill"
        :with-rounded="true"
        :with-surface="false"
        :with-overflow="false"
        max-height="auto"
        placement="top-end"
        bg-color="transparent"
        :offset-y="18"
        :offset-x="12"
      >
        <template #control>
          <BaseIcon
            name="vuesax:setting-2-linear"
            color="var(--color-foreground-inverted)"
            size="24px"
            clickable
          />
        </template>

        <template #dropdown>
          <BaseIsland variant="transparent-inverted-200" size="sm" :with-shadow="false">
            <BaseGroupActions gap="xs" direction="column">
              <BaseButton
                v-for="quality in qualitiesMap"
                :key="quality.name"
                size="mini"
                :variant="currentQuality === quality.name ? 'dark' : 'transparent'"
                with-full
                @click="handleChangeQuality(quality)"
              >
                {{ qualitiesMap[quality.name].label }}
              </BaseButton>
            </BaseGroupActions>
          </BaseIsland>
        </template>
      </BaseDropdownWrapper>
      <BaseIcon
        v-else
        name="vuesax:setting-2-linear"
        color="var(--color-foreground-inverted)"
        size="24px"
        style="opacity: 0"
      />
    </div>
    <div class="media-player-controls__timeline">
      <BaseTypography
        type="text"
        color="var(--color-foreground-inverted)"
      >
        {{ formatTime(props.currentTime) }}
      </BaseTypography>

      <div class="media-player-controls__progress-container" @click="emit('seekTo', $event)">
        <BaseProgressLinear
          :progress="props.playProgress"
          height="4px"
          variant="light"
          :animated="false"
        />
      </div>

      <BaseTypography
        type="text"
        color="var(--color-foreground-inverted)"
      >
        {{ formatTime(props.duration) }}
      </BaseTypography>
    </div>
  </div>
</template>

<script setup lang="ts">
// types
import type { IMediaDropdownOption } from '../model/const'

import { formatTime } from '~/shared/lib'
import { BaseButton, BaseDropdownWrapper, BaseGroupActions, BaseIcon, BaseIsland, BaseProgressLinear, BaseTypography } from '~/shared/ui'
import { qualitiesMap, Quality, Speed, speedsMap } from '../model/const'

interface Props {
  currentTime: number
  duration: number
  playProgress: number
  isPlaying: boolean
  isLocal: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'seekTo', event: MouseEvent): void
  (e: 'togglePlay'): void
  (e: 'changeQuality', quality: number): void
  (e: 'changeSpeed', speed: number): void
}>()

const currentQuality = ref<Quality>(Quality.QAuto)
const currentSpeed = ref<Speed>(Speed.Speed1)

function handleChangeQuality(quality: IMediaDropdownOption<Quality>) {
  currentQuality.value = quality.name

  emit('changeQuality', quality.value)
}

function handleChangeSpeed(speed: IMediaDropdownOption<Speed>) {
  currentSpeed.value = speed.name

  emit('changeSpeed', speed.value)
}
</script>

<style scoped lang="scss">
.media-player-controls {
  background-color: var(--color-surface-transparent-inverted-900);
  border-radius: var(--radius-md-alt);
  display: flex;
  flex-direction: column;
  gap: rem(12px);
  padding: rem(16px);

  @include bp-media('xs', 'sm', 'md') {
    gap: rem(8px);
    padding: rem(12px);
  }

  &__controls {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: rem(12px);

    &:deep(.iconify) {
      @include bp-media('xs', 'sm') {
        font-size: 22px !important;
      }
    }
  }

  &__timeline {
    display: flex;
    align-items: center;
    gap: rem(10px);

    :deep(.base-typography) {
      width: 48px;
      text-align: center;

      @include bp-media('xs', 'sm', 'md') {
        font-size: 12px;
        width: 32px;
      }
    }
  }

  &__progress-container {
    flex: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
  }
}
</style>
